/**
 * LocationHierarchySidebar
 * Hierarchical tree panel for Region → Country → Location navigation
 * Uses simple div structure (NOT shadcn Sidebar) to avoid nesting conflicts
 */

import * as React from 'react';
import { ChevronRight, Globe, MapPin, MoreVertical, Plus, Edit, Trash2, X, Layers } from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button, Badge, ConfirmDialog, ScrollArea } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { Region, Country, Location, Cluster } from '@simcheck/shared-types';
import { deleteRegion, deleteCountry } from '@/services/hierarchicalLocationService';
import { deleteCluster } from '@/services/clusterService';
import { useToast } from '@/contexts/ToastContext';

interface LocationHierarchySidebarProps {
  regions: Region[];
  countries: Country[];
  locations: Location[];
  clusters: Cluster[];
  selectedRegionId?: string | null;
  selectedCountryId?: string | null;
  selectedLocationId?: string | null;
  selectedClusterId?: string | null;
  onSelectRegion: (regionId: string | null) => void;
  onSelectCountry: (countryId: string | null) => void;
  onSelectLocation: (locationId: string | null) => void;
  onSelectCluster: (clusterId: string | null) => void;
  onEditRegion: (region: Region) => void;
  onEditCountry: (country: Country) => void;
  onEditLocation: (location: Location) => void;
  onEditCluster: (cluster: Cluster) => void;
  onCreateRegion: () => void;
  onCreateCountry: () => void;
  onCreateCluster: () => void;
  onRefresh: () => void;
  isSuperAdmin: boolean;
  /**
   * Per-location edit gate (#202). Defaults to super-admin-only so existing
   * callers keep their behaviour; the Locations page passes the same predicate
   * it uses for the table row and the map, so a tenant admin can edit their own
   * org's locations here but never a shared one.
   */
  canEditLocation?: (location: Location) => boolean;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

export function LocationHierarchySidebar({
  regions,
  countries,
  locations,
  clusters,
  selectedRegionId,
  selectedCountryId,
  selectedLocationId,
  selectedClusterId,
  onSelectRegion,
  onSelectCountry,
  onSelectLocation,
  onSelectCluster,
  onEditRegion,
  onEditCountry,
  onEditLocation,
  onEditCluster,
  onCreateRegion,
  onCreateCountry,
  onCreateCluster,
  onRefresh,
  isSuperAdmin,
  canEditLocation,
  isCollapsed = false,
  onToggleCollapse,
}: LocationHierarchySidebarProps) {
  const { success, error: showError } = useToast();
  const [deleteRegionConfirm, setDeleteRegionConfirm] = React.useState<Region | null>(null);
  const [deleteCountryConfirm, setDeleteCountryConfirm] = React.useState<Country | null>(null);
  const [deleteClusterConfirm, setDeleteClusterConfirm] = React.useState<Cluster | null>(null);
  const [expandedRegions, setExpandedRegions] = React.useState<Set<string>>(new Set());
  const [expandedCountries, setExpandedCountries] = React.useState<Set<string>>(new Set());
  const [clustersExpanded, setClustersExpanded] = React.useState(true);

  // Get countries for a region
  const getCountriesForRegion = (regionId: string) =>
    countries.filter(c => c.regionId === regionId);

  // Get locations for a country
  const getLocationsForCountry = (countryId: string) =>
    locations.filter(l => l.countryId === countryId);

  // Toggle region expansion
  const toggleRegion = (regionId: string) => {
    const newExpanded = new Set(expandedRegions);
    if (newExpanded.has(regionId)) {
      newExpanded.delete(regionId);
    } else {
      newExpanded.add(regionId);
    }
    setExpandedRegions(newExpanded);
  };

  // Toggle country expansion
  const toggleCountry = (countryId: string) => {
    const newExpanded = new Set(expandedCountries);
    if (newExpanded.has(countryId)) {
      newExpanded.delete(countryId);
    } else {
      newExpanded.add(countryId);
    }
    setExpandedCountries(newExpanded);
  };

  // Handle region deletion
  const handleDeleteRegion = async () => {
    if (!deleteRegionConfirm) return;
    try {
      await deleteRegion(deleteRegionConfirm.id);
      success('Region deleted successfully');
      onRefresh();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete region');
    } finally {
      setDeleteRegionConfirm(null);
    }
  };

  // Handle country deletion
  const handleDeleteCountry = async () => {
    if (!deleteCountryConfirm) return;
    try {
      await deleteCountry(deleteCountryConfirm.id);
      success('Country deleted successfully');
      onRefresh();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete country');
    } finally {
      setDeleteCountryConfirm(null);
    }
  };

  // Handle cluster deletion
  const handleDeleteCluster = async () => {
    if (!deleteClusterConfirm) return;
    try {
      await deleteCluster(deleteClusterConfirm.id);
      success('Cluster deleted successfully');
      onRefresh();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete cluster');
    } finally {
      setDeleteClusterConfirm(null);
    }
  };

  const clearSelection = () => {
    onSelectRegion(null);
    onSelectCountry(null);
    onSelectLocation(null);
    onSelectCluster(null);
  };

  if (isCollapsed) {
    return null;
  }

  return (
    <>
      <div className="flex flex-col h-full bg-background">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-border">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm text-foreground">Locations</span>
            <Badge variant="outline" className="text-xs">
              {locations.length}
            </Badge>
          </div>
          {onToggleCollapse && (
            <Button variant="ghost" size="sm" onClick={onToggleCollapse} className="h-6 w-6 p-0">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Hierarchy Label */}
        <div className="flex items-center justify-between px-3 py-2 text-xs text-muted-foreground font-medium uppercase tracking-wider">
          <span>Hierarchy</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-5 px-2 text-xs"
            onClick={clearSelection}
          >
            Clear
          </Button>
        </div>

        {/* Scrollable Tree */}
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            {regions.map((region) => {
              const regionCountries = getCountriesForRegion(region.id);
              const isExpanded = expandedRegions.has(region.id);
              const isSelected = selectedRegionId === region.id;

              return (
                <div key={region.id}>
                  <Collapsible
                    open={isExpanded}
                    onOpenChange={() => toggleRegion(region.id)}
                  >
                    <div className="flex items-center group">
                      <CollapsibleTrigger asChild>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectRegion(region.id);
                          }}
                          className={cn(
                            'flex items-center gap-2 flex-1 px-2 py-1.5 rounded text-sm hover:bg-muted transition-colors',
                            isSelected && 'bg-accent text-accent-foreground'
                          )}
                        >
                          <ChevronRight
                            className={cn(
                              'h-4 w-4 transition-transform text-muted-foreground',
                              isExpanded && 'rotate-90'
                            )}
                          />
                          <Globe className="h-4 w-4 text-accent-blue" />
                          <span className="flex-1 text-left truncate">{region.name}</span>
                          <Badge variant="secondary" className="text-xs">
                            {region.countryCount || regionCountries.length}
                          </Badge>
                        </button>
                      </CollapsibleTrigger>

                      {isSuperAdmin && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100"
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => onEditRegion(region)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => setDeleteRegionConfirm(region)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>

                    <CollapsibleContent>
                      <div className="ml-4 pl-2 border-l border-border space-y-1 mt-1">
                        {regionCountries.map((country) => {
                          const countryLocations = getLocationsForCountry(country.id);
                          const isCountryExpanded = expandedCountries.has(country.id);
                          const isCountrySelected = selectedCountryId === country.id;

                          return (
                            <div key={country.id}>
                              <Collapsible
                                open={isCountryExpanded}
                                onOpenChange={() => toggleCountry(country.id)}
                              >
                                <div className="flex items-center group">
                                  <CollapsibleTrigger asChild>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onSelectCountry(country.id);
                                      }}
                                      className={cn(
                                        'flex items-center gap-2 flex-1 px-2 py-1 rounded text-sm hover:bg-muted transition-colors',
                                        isCountrySelected && 'bg-accent text-accent-foreground'
                                      )}
                                    >
                                      <ChevronRight
                                        className={cn(
                                          'h-3 w-3 transition-transform text-muted-foreground',
                                          isCountryExpanded && 'rotate-90'
                                        )}
                                      />
                                      <span className="text-base">{country.flag || '🏳️'}</span>
                                      <span className="flex-1 text-left truncate">{country.name}</span>
                                      <Badge variant="outline" className="text-xs">
                                        {country.locationCount || countryLocations.length}
                                      </Badge>
                                    </button>
                                  </CollapsibleTrigger>

                                  {isSuperAdmin && (
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100"
                                        >
                                          <MoreVertical className="h-3 w-3" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        <DropdownMenuItem onClick={() => onEditCountry(country)}>
                                          <Edit className="h-4 w-4 mr-2" />
                                          Edit
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                          className="text-destructive"
                                          onClick={() => setDeleteCountryConfirm(country)}
                                        >
                                          <Trash2 className="h-4 w-4 mr-2" />
                                          Delete
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  )}
                                </div>

                                <CollapsibleContent>
                                  <div className="ml-4 pl-2 border-l border-border space-y-0.5 mt-1">
                                    {countryLocations.map((location) => {
                                      const isLocationSelected = selectedLocationId === location.id;

                                      return (
                                        <div key={location.id} className="flex items-center group">
                                          <button
                                            onClick={() => onSelectLocation(location.id)}
                                            className={cn(
                                              'flex items-center gap-2 flex-1 px-2 py-1 rounded text-xs hover:bg-muted transition-colors',
                                              isLocationSelected && 'bg-accent text-accent-foreground'
                                            )}
                                          >
                                            <MapPin className="h-3 w-3 text-primary" />
                                            <span className="flex-1 text-left truncate">
                                              {location.displayName || location.name}
                                            </span>
                                          </button>
                                          {(canEditLocation ? canEditLocation(location) : isSuperAdmin) && (
                                            <DropdownMenu>
                                              <DropdownMenuTrigger asChild>
                                                <Button
                                                  variant="ghost"
                                                  size="sm"
                                                  className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100"
                                                >
                                                  <MoreVertical className="h-3 w-3" />
                                                </Button>
                                              </DropdownMenuTrigger>
                                              <DropdownMenuContent align="end">
                                                <DropdownMenuItem onClick={() => onEditLocation(location)}>
                                                  <Edit className="h-4 w-4 mr-2" />
                                                  Edit
                                                </DropdownMenuItem>
                                              </DropdownMenuContent>
                                            </DropdownMenu>
                                          )}
                                        </div>
                                      );
                                    })}

                                    {countryLocations.length === 0 && (
                                      <div className="px-2 py-2 text-xs text-muted-foreground italic">
                                        No locations
                                      </div>
                                    )}
                                  </div>
                                </CollapsibleContent>
                              </Collapsible>
                            </div>
                          );
                        })}

                        {regionCountries.length === 0 && (
                          <div className="px-2 py-2 text-xs text-muted-foreground italic">
                            No countries
                          </div>
                        )}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              );
            })}

            {regions.length === 0 && (
              <div className="px-2 py-8 text-center text-muted-foreground">
                <Globe className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No regions yet</p>
                {isSuperAdmin && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={onCreateRegion}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Region
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Clusters Section */}
          <div className="mt-4 border-t border-border pt-2">
            <Collapsible open={clustersExpanded} onOpenChange={setClustersExpanded}>
              <div className="flex items-center justify-between px-3 py-2">
                <CollapsibleTrigger asChild>
                  <button className="flex items-center gap-2 text-xs text-muted-foreground font-medium uppercase tracking-wider hover:text-foreground transition-colors">
                    <ChevronRight
                      className={cn(
                        'h-3 w-3 transition-transform',
                        clustersExpanded && 'rotate-90'
                      )}
                    />
                    <Layers className="h-3 w-3" />
                    <span>Clusters</span>
                    <Badge variant="outline" className="text-xs ml-1">
                      {clusters.length}
                    </Badge>
                  </button>
                </CollapsibleTrigger>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 w-5 p-0"
                  onClick={onCreateCluster}
                  title="Create Cluster"
                >
                  <Plus className="h-3 w-3" />
                </Button>
              </div>

              <CollapsibleContent>
                <div className="px-2 space-y-1">
                  {clusters.map((cluster) => {
                    const isSelected = selectedClusterId === cluster.id;

                    return (
                      <div key={cluster.id} className="flex items-center group">
                        <button
                          onClick={() => onSelectCluster(cluster.id)}
                          className={cn(
                            'flex items-center gap-2 flex-1 px-2 py-1.5 rounded text-sm hover:bg-muted transition-colors',
                            isSelected && 'bg-accent text-accent-foreground'
                          )}
                        >
                          <Layers className="h-4 w-4 text-accent-purple" />
                          <span className="flex-1 text-left truncate">{cluster.name}</span>
                          <Badge variant="secondary" className="text-xs">
                            {cluster.items?.length || 0}
                          </Badge>
                        </button>

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100"
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => onEditCluster(cluster)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => setDeleteClusterConfirm(cluster)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    );
                  })}

                  {clusters.length === 0 && (
                    <div className="px-2 py-4 text-center text-muted-foreground">
                      <Layers className="h-6 w-6 mx-auto mb-2 opacity-50" />
                      <p className="text-xs">No clusters yet</p>
                      <p className="text-xs mt-1">Create clusters to group locations</p>
                    </div>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </ScrollArea>

        {/* Footer - Create buttons */}
        {isSuperAdmin && (
          <div className="border-t border-border p-2 flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="flex-1 text-xs"
              onClick={onCreateRegion}
            >
              <Plus className="h-3 w-3 mr-1" />
              Region
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="flex-1 text-xs"
              onClick={onCreateCountry}
            >
              <Plus className="h-3 w-3 mr-1" />
              Country
            </Button>
          </div>
        )}
      </div>

      {/* Delete Region Confirmation */}
      <ConfirmDialog
        open={!!deleteRegionConfirm}
        onClose={() => setDeleteRegionConfirm(null)}
        onConfirm={handleDeleteRegion}
        title="Delete Region"
        message={`Are you sure you want to delete "${deleteRegionConfirm?.name}"? This action cannot be undone. You must first delete or reassign all countries in this region.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
      />

      {/* Delete Country Confirmation */}
      <ConfirmDialog
        open={!!deleteCountryConfirm}
        onClose={() => setDeleteCountryConfirm(null)}
        onConfirm={handleDeleteCountry}
        title="Delete Country"
        message={`Are you sure you want to delete "${deleteCountryConfirm?.name}"? This action cannot be undone. You must first delete or reassign all locations in this country.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
      />

      {/* Delete Cluster Confirmation */}
      <ConfirmDialog
        open={!!deleteClusterConfirm}
        onClose={() => setDeleteClusterConfirm(null)}
        onConfirm={handleDeleteCluster}
        title="Delete Cluster"
        message={`Are you sure you want to delete "${deleteClusterConfirm?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
      />
    </>
  );
}
